import { useState, useEffect } from 'react'
import {
  useFeature,
  useCreateFeature,
  useUpdateFeature,
  type FeatureCategory,
  type AttestationFramework,
  type CreateFeatureRequest,
  type UpdateFeatureRequest,
} from '@/hooks/useAdminFeatures'

interface FeatureEditorProps {
  featureKey?: string | null // If provided, edit mode; otherwise create mode
  onClose: () => void
  onSuccess?: () => void
}

const CATEGORIES: { value: FeatureCategory; label: string }[] = [
  { value: 'product', label: 'Product' },
  { value: 'portal', label: 'Portal' },
  { value: 'admin', label: 'Admin' },
  { value: 'distillation', label: 'Distillation' },
  { value: 'limit', label: 'Limit' },
]

const ATTESTATION_FRAMEWORKS: { value: AttestationFramework; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'HIPAA', label: 'HIPAA' },
  { value: 'EEOC', label: 'EEOC' },
  { value: 'clinical', label: 'Clinical' },
  { value: 'regulatory', label: 'Regulatory' },
]

export default function FeatureEditor({ featureKey, onClose, onSuccess }: FeatureEditorProps) {
  const isEditMode = !!featureKey
  const { data: existingFeature, isLoading } = useFeature(featureKey || null)
  const createMutation = useCreateFeature()
  const updateMutation = useUpdateFeature()

  // Form state
  const [key, setKey] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<FeatureCategory>('product')
  const [requiresTrial, setRequiresTrial] = useState(false)
  const [requiresPro, setRequiresPro] = useState(false)
  const [requiresEnterprise, setRequiresEnterprise] = useState(false)
  const [attestationFramework, setAttestationFramework] = useState<AttestationFramework>('none')
  const [isPremium, setIsPremium] = useState(false)
  const [displayOrder, setDisplayOrder] = useState(0)

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Initialize form when editing
  useEffect(() => {
    if (existingFeature) {
      setKey(existingFeature.key)
      setName(existingFeature.name)
      setDescription(existingFeature.description)
      setCategory(existingFeature.category)
      setRequiresTrial(existingFeature.requires_trial)
      setRequiresPro(existingFeature.requires_pro)
      setRequiresEnterprise(existingFeature.requires_enterprise)
      setAttestationFramework(existingFeature.attestation_framework || 'none')
      setIsPremium(existingFeature.is_premium)
      setDisplayOrder(existingFeature.display_order)
    }
  }, [existingFeature])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!key.trim()) {
      newErrors.key = 'Feature key is required'
    } else if (!/^[a-z0-9_]+$/.test(key)) {
      newErrors.key = 'Key must contain only lowercase letters, numbers, and underscores'
    }

    if (!name.trim()) {
      newErrors.name = 'Feature name is required'
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    try {
      if (isEditMode) {
        const updateData: UpdateFeatureRequest = {
          name,
          description,
          category,
          requires_trial: requiresTrial,
          requires_pro: requiresPro,
          requires_enterprise: requiresEnterprise,
          attestation_framework: attestationFramework === 'none' ? null : attestationFramework,
          is_premium: isPremium,
          display_order: displayOrder,
        }
        await updateMutation.mutateAsync({ key: featureKey!, data: updateData })
      } else {
        const createData: CreateFeatureRequest = {
          key,
          name,
          description,
          category,
          requires_trial: requiresTrial,
          requires_pro: requiresPro,
          requires_enterprise: requiresEnterprise,
          attestation_framework: attestationFramework === 'none' ? null : attestationFramework,
          is_premium: isPremium,
          display_order: displayOrder,
        }
        await createMutation.mutateAsync(createData)
      }

      onSuccess?.()
      onClose()
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Operation failed'
      setErrors({ submit: errorMessage })
    }
  }

  if (isLoading && isEditMode) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <div className="bg-[#0a0e14] border border-[#FFB84D]/30 rounded-lg p-8">
          <div className="text-[#FFB84D] font-mono animate-pulse">
            [LOADING FEATURE...]
          </div>
        </div>
      </div>
    )
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div
          className="bg-[#0a0e14] border border-[#FFB84D]/30 rounded-lg shadow-[0_0_50px_rgba(255,184,77,0.2)]
                     w-full max-w-2xl my-8"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-[#FFB84D]/20 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">⚙️</span>
                <h2 className="text-xl font-mono uppercase tracking-wider text-[#FFB84D]">
                  {isEditMode ? 'Edit Feature' : 'Create Feature'}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-[#8b949e] hover:text-[#FFB84D] transition-colors text-2xl"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Key */}
            <div>
              <label className="block text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">
                Feature Key <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={key}
                onChange={(e) => setKey(e.target.value.toLowerCase())}
                disabled={isEditMode}
                className={`w-full bg-[#141c24] border rounded px-3 py-2
                           text-[#e8eaed] font-mono text-sm
                           focus:outline-none focus:border-[#FFB84D]/50
                           ${isEditMode ? 'opacity-60 cursor-not-allowed' : ''}
                           ${errors.key ? 'border-red-500' : 'border-[#2a3542]'}`}
                placeholder="e.g., voice_pipeline_access"
              />
              {errors.key && (
                <div className="mt-1 text-xs text-red-400 font-mono">{errors.key}</div>
              )}
              {isEditMode && (
                <div className="mt-1 text-xs text-[#8b949e] font-mono">
                  Feature key cannot be changed after creation
                </div>
              )}
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">
                Display Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`w-full bg-[#141c24] border rounded px-3 py-2
                           text-[#e8eaed] font-mono text-sm
                           focus:outline-none focus:border-[#FFB84D]/50
                           ${errors.name ? 'border-red-500' : 'border-[#2a3542]'}`}
                placeholder="e.g., Voice Pipeline Access"
              />
              {errors.name && (
                <div className="mt-1 text-xs text-red-400 font-mono">{errors.name}</div>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className={`w-full bg-[#141c24] border rounded px-3 py-2
                           text-[#e8eaed] font-mono text-sm leading-relaxed
                           focus:outline-none focus:border-[#FFB84D]/50
                           resize-y
                           ${errors.description ? 'border-red-500' : 'border-[#2a3542]'}`}
                placeholder="Describe what this feature provides..."
              />
              {errors.description && (
                <div className="mt-1 text-xs text-red-400 font-mono">{errors.description}</div>
              )}
            </div>

            {/* Category & Display Order */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as FeatureCategory)}
                  className="w-full bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                             text-[#e8eaed] font-mono text-sm
                             focus:outline-none focus:border-[#FFB84D]/50"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">
                  Display Order
                </label>
                <input
                  type="number"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                  className="w-full bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                             text-[#e8eaed] font-mono text-sm
                             focus:outline-none focus:border-[#FFB84D]/50"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Plan Requirements */}
            <div>
              <label className="block text-xs text-[#8b949e] font-mono mb-3 uppercase tracking-wider">
                Plan Requirements
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={requiresTrial}
                    onChange={(e) => setRequiresTrial(e.target.checked)}
                    className="w-4 h-4 bg-[#141c24] border border-[#2a3542] rounded
                               checked:bg-[#FFB84D] checked:border-[#FFB84D]
                               focus:outline-none focus:ring-2 focus:ring-[#FFB84D]/50"
                  />
                  <span className="text-sm text-[#e8eaed] font-mono group-hover:text-[#FFB84D]">
                    Trial Plan
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={requiresPro}
                    onChange={(e) => setRequiresPro(e.target.checked)}
                    className="w-4 h-4 bg-[#141c24] border border-[#2a3542] rounded
                               checked:bg-[#FFB84D] checked:border-[#FFB84D]
                               focus:outline-none focus:ring-2 focus:ring-[#FFB84D]/50"
                  />
                  <span className="text-sm text-[#e8eaed] font-mono group-hover:text-[#FFB84D]">
                    Pro Plan
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={requiresEnterprise}
                    onChange={(e) => setRequiresEnterprise(e.target.checked)}
                    className="w-4 h-4 bg-[#141c24] border border-[#2a3542] rounded
                               checked:bg-[#FFB84D] checked:border-[#FFB84D]
                               focus:outline-none focus:ring-2 focus:ring-[#FFB84D]/50"
                  />
                  <span className="text-sm text-[#e8eaed] font-mono group-hover:text-[#FFB84D]">
                    Enterprise Plan
                  </span>
                </label>
              </div>
            </div>

            {/* Attestation Framework */}
            <div>
              <label className="block text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">
                Attestation Framework
              </label>
              <select
                value={attestationFramework}
                onChange={(e) => setAttestationFramework(e.target.value as AttestationFramework)}
                className="w-full bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                           text-[#e8eaed] font-mono text-sm
                           focus:outline-none focus:border-[#FFB84D]/50"
              >
                {ATTESTATION_FRAMEWORKS.map((framework) => (
                  <option key={framework.value} value={framework.value}>
                    {framework.label}
                  </option>
                ))}
              </select>
              <div className="mt-1 text-xs text-[#8b949e] font-mono">
                Requires compliance attestation to access
              </div>
            </div>

            {/* Premium Flag */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={isPremium}
                  onChange={(e) => setIsPremium(e.target.checked)}
                  className="w-4 h-4 bg-[#141c24] border border-[#2a3542] rounded
                             checked:bg-[#FFB84D] checked:border-[#FFB84D]
                             focus:outline-none focus:ring-2 focus:ring-[#FFB84D]/50"
                />
                <div>
                  <span className="text-sm text-[#e8eaed] font-mono group-hover:text-[#FFB84D]">
                    Premium Feature
                  </span>
                  <div className="text-xs text-[#8b949e] font-mono">
                    Marks feature as premium/advanced functionality
                  </div>
                </div>
              </label>
            </div>

            {/* Error Message */}
            {errors.submit && (
              <div className="bg-red-900/20 border border-red-500/30 rounded p-3">
                <div className="text-sm text-red-400 font-mono">{errors.submit}</div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#2a3542]">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 bg-[#141c24] border border-[#2a3542] rounded
                           text-[#8b949e] font-mono text-sm uppercase tracking-wider
                           hover:border-[#FFB84D]/30 hover:text-[#FFB84D]
                           transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-5 py-2 bg-[#FFB84D] rounded
                           text-[#0a0e14] font-mono text-sm uppercase tracking-wider font-bold
                           hover:bg-[#FFB84D]/90 hover:shadow-[0_0_20px_rgba(255,184,77,0.3)]
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200"
              >
                {isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
